/**
 * The save-status store: who is saving, who just saved, who couldn't.
 *
 * One entry per *source* (an editor instance, a mutation). The sidebar's
 * status line shows the worst state across them — failed > saving > saved >
 * idle — so one editor's success can never hide another's failure. A failed
 * entry carries its own retry and is never cleared by unmount: it persists,
 * with Retry and a dismiss control, until it succeeds or the user dismisses
 * it. `saved` lingers for `SAVED_LINGER_MS` and then the entry clears.
 *
 * Module-level and framework-free, in the shape of `shared/offline/outboxState`:
 * `subscribe` + `getSnapshot` feed `useSyncExternalStore`; nothing here is
 * persisted. Designed to grow into the system's single quiet voice, never into
 * toasts (nav spec §4.5).
 */

export type SourceState = 'saving' | 'saved' | 'failed';

/** One reporter's entry: an editor instance or a single mutation. */
export interface StatusSource {
  id: string;
  /** What the user calls this source: "Journal", the document title, … */
  label: string;
  state: SourceState;
  /** Failed only: replays the write without needing the reporter to still exist. */
  retry?: () => Promise<void> | void;
  since: number;
}

/** The whole store at one instant; a new object on every change. */
export interface StatusSnapshot {
  sources: readonly StatusSource[];
}

/** The line's one state: idle, or the worst source state. */
export type StatusLevel = 'idle' | SourceState;

/** What the status line renders: a level, its text, and the failures behind it. */
export interface StatusSummary {
  level: StatusLevel;
  text: string;
  failed: readonly StatusSource[];
}

/** How long "Saved · just now" stays before the entry clears. */
export const SAVED_LINGER_MS = 4_000;

let snapshot: StatusSnapshot = { sources: [] };
const listeners = new Set<() => void>();
const lingerTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Tell every subscriber the snapshot moved. */
function emit(): void {
  for (const listener of listeners) listener();
}

/** Swap in a new sources list and notify. */
function replace(sources: readonly StatusSource[]): void {
  snapshot = { sources };
  emit();
}

/** Stop a pending "saved" auto-clear, if one is scheduled for this id. */
function cancelLinger(id: string): void {
  const timer = lingerTimers.get(id);
  if (timer) clearTimeout(timer);
  lingerTimers.delete(id);
}

/** Insert or update one source, keeping first-report order. */
function upsert(next: StatusSource): void {
  const index = snapshot.sources.findIndex(s => s.id === next.id);
  const sources = [...snapshot.sources];
  if (index === -1) sources.push(next);
  else sources[index] = next;
  replace(sources);
}

/** A write is in flight. */
export function reportSaving(id: string, label: string): void {
  cancelLinger(id);
  upsert({ id, label, state: 'saving', since: Date.now() });
}

/** A write landed; show it briefly, then forget it. */
export function reportSaved(id: string, label: string): void {
  cancelLinger(id);
  upsert({ id, label, state: 'saved', since: Date.now() });
  lingerTimers.set(
    id,
    setTimeout(() => {
      lingerTimers.delete(id);
      const current = snapshot.sources.find(s => s.id === id);
      if (current?.state === 'saved') clearSource(id);
    }, SAVED_LINGER_MS)
  );
}

/** A write failed. `retry` must not depend on the reporter still being mounted. */
export function reportFailed(id: string, label: string, retry?: () => Promise<void> | void): void {
  cancelLinger(id);
  upsert({ id, label, state: 'failed', since: Date.now(), ...(retry && { retry }) });
}

/** Forget a source: on success, on discard, or when the user dismisses a failure. */
export function clearSource(id: string): void {
  cancelLinger(id);
  if (!snapshot.sources.some(s => s.id === id)) return;
  replace(snapshot.sources.filter(s => s.id !== id));
}

/** Run every failed source's retry. Each starts now and reports its own outcome; one throwing retry does not stop the rest. */
export async function retryAll(): Promise<void> {
  const failed = snapshot.sources.filter(s => s.state === 'failed' && s.retry);
  const settled: Promise<unknown>[] = [];
  for (const s of failed) {
    try {
      settled.push(Promise.resolve(s.retry?.()));
    } catch {
      // A synchronous throw is one source's problem, not the line's.
    }
  }
  await Promise.allSettled(settled);
}

/** Subscribe to every change; returns the unsubscribe. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current snapshot; referentially stable until the next change. */
export function getSnapshot(): StatusSnapshot {
  return snapshot;
}

/** Tests only: drop every entry, timer, and listener. */
export function resetSaveStatusStore(): void {
  for (const id of [...lingerTimers.keys()]) cancelLinger(id);
  snapshot = { sources: [] };
  listeners.clear();
}

const RANK: Record<SourceState, number> = { saved: 1, saving: 2, failed: 3 };

/**
 * The line's one state and one text. Worst wins; several failures are counted.
 * `syncing` is the outbox draining — it reads as saving because that is what
 * it is, and it never outranks a failure.
 */
export function summarize(sources: readonly StatusSource[], syncing: boolean): StatusSummary {
  const failed = sources.filter(s => s.state === 'failed');
  if (failed.length > 0) {
    const text = failed.length === 1 ? "Couldn't save" : `${failed.length} couldn't save`;
    return { level: 'failed', text, failed };
  }
  let worst: SourceState | undefined;
  for (const s of sources) {
    if (!worst || RANK[s.state] > RANK[worst]) worst = s.state;
  }
  if (worst === 'saving' || syncing) {
    return { level: 'saving', text: worst === 'saving' ? 'Saving…' : 'Syncing…', failed };
  }
  if (worst === 'saved') return { level: 'saved', text: 'Saved · just now', failed };
  return { level: 'idle', text: 'Up to date', failed };
}
